"use client";

import React from 'react';
import { useRouter } from 'next/navigation';
import Skeleton from './ui/Skeleton';
import { WalletAvatar } from './WalletIdenticon';
import { MicIcon, ClockIcon, CloseIcon } from '../../components/shared/icons';

// Minimal local typings for the (non-standard, not in lib.dom.d.ts) Web Speech
// API. Avoids depending on `window.SpeechRecognition`, which TypeScript's
// default DOM lib does not declare.
type SpeechRecognitionResultLike = { transcript: string };
type SpeechRecognitionEventLike = {
  results?: ArrayLike<ArrayLike<SpeechRecognitionResultLike>>;
};
type SpeechRecognitionLike = {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  start: () => void;
  stop?: () => void;
};
type SpeechRecognitionConstructorLike = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionConstructorLike | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructorLike;
    webkitSpeechRecognition?: SpeechRecognitionConstructorLike;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

type SearchResult = {
  kind?: 'wallet' | 'tx';
  address?: string;
  chain?: string;
  network?: string;
  id?: string;
  name?: string;
  ens?: string;
  summary?: string;
  description?: string;
  hash?: string;
  from?: string;
  to?: string | null;
  socials?: { displayName?: string; avatarUrl?: string };
  trustScore?: number;
  riskScore?: number;
  flagsCount?: number;
  blacklisted?: boolean;
  kycStatus?: string;
  statusTags?: string[];
};

type SearchApiTx = {
  chain: string;
  hash: string;
  from: string;
  to: string | null;
  valueEther: string;
  blockNumber: number | null;
};

type SearchApiResponse = {
  kind?: 'wallet' | 'tx';
  results?: SearchResult[];
  tx?: SearchApiTx | null;
};

type ResolveWalletResponse =
  | {
      kind?: 'wallet';
      address: string;
      resolvedFrom: string;
      chainHint?: string;
    }
  | {
      kind: 'lightning';
      input: string;
      route: string;
    };

type Props = {
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  autoFocus?: boolean;
  showSuggestions?: boolean;
  maxSuggestions?: number;
  /** Shows a mic button that fills the input via the browser's SpeechRecognition API, when supported. Opt-in, no-op fallback otherwise. */
  showVoiceButton?: boolean;
  /** Persists submitted/clicked queries to localStorage and renders a "recent searches" chip row while the input is empty. Opt-in. */
  enableRecentSearches?: boolean;
  recentSearchesLimit?: number;
  /**
   * Visual shape only - no change to search/resolve logic. 'pill' (default)
   * is the original fully-rounded bar with a "Go" button, used in the
   * header and elsewhere. 'panel' is a softer rounded-2xl bar with a
   * rounded-xl button, used by the homepage's large search section to match
   * the client's reference mockup exactly.
   */
  variant?: 'pill' | 'panel';
  /** Overrides the submit button's idle label (defaults to "Go" for pill, "Search" for panel). */
  submitLabel?: string;
};

const RECENT_SEARCHES_KEY = 'reqrium:recent-searches';

function normalizeChain(chain?: string | null): string {
  const c = String(chain || '').toLowerCase();
  if (!c) return '';
  if (c === 'eth') return 'ethereum';
  if (c === 'btc') return 'bitcoin';
  if (c === 'sol') return 'solana';
  return c;
}

function defaultChain(): string {
  // Only NEXT_PUBLIC_* is available in the browser.
  return normalizeChain(process.env.NEXT_PUBLIC_DEFAULT_CHAIN || 'ethereum') || 'ethereum';
}

function toWalletPath(chain: string, address: string): string {
  return `/wallet/${encodeURIComponent(chain)}/${encodeURIComponent(address)}`;
}

function toTxPath(chain: string, hash: string): string {
  return `/tx/${encodeURIComponent(chain)}/${encodeURIComponent(hash)}`;
}

export default function WalletSearchBar({
  className,
  inputClassName,
  placeholder = 'Search wallet or tx hash (address, ENS, UD, 0x… tx)',
  size = 'md',
  autoFocus,
  showSuggestions = true,
  maxSuggestions = 5,
  showVoiceButton = false,
  enableRecentSearches = false,
  recentSearchesLimit = 6,
  variant = 'pill',
  submitLabel,
}: Props) {
  const router = useRouter();
  const [q, setQ] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [suggestions, setSuggestions] = React.useState<SearchResult[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = React.useState(false);
  // FIX-30: tracks the debounced suggestions fetch specifically (separate from
  // `loading`, which is for the full-form submit) so the dropdown can show a
  // skeleton instead of sitting empty while results come in.
  const [suggestionsLoading, setSuggestionsLoading] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);

  // Voice search: progressive enhancement only, no-op when unsupported.
  const [voiceSupported, setVoiceSupported] = React.useState(false);
  const [listening, setListening] = React.useState(false);
  const recognitionRef = React.useRef<SpeechRecognitionLike | null>(null);

  // Recent searches: client-only, opt-in via `enableRecentSearches`.
  const [recent, setRecent] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!showVoiceButton) return;
    setVoiceSupported(!!getSpeechRecognitionCtor());
  }, [showVoiceButton]);

  React.useEffect(() => {
    if (!enableRecentSearches) return;
    try {
      const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setRecent(parsed.filter((x) => typeof x === 'string').slice(0, recentSearchesLimit));
      }
    } catch {
      // ignore - localStorage may be unavailable (private mode, etc.)
    }
  }, [enableRecentSearches, recentSearchesLimit]);

  const pushRecentSearch = React.useCallback(
    (query: string) => {
      if (!enableRecentSearches) return;
      const trimmed = query.trim();
      if (!trimmed) return;
      setRecent((prev) => {
        const next = [trimmed, ...prev.filter((r) => r.toLowerCase() !== trimmed.toLowerCase())].slice(
          0,
          recentSearchesLimit
        );
        try {
          window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
        } catch {
          // ignore
        }
        return next;
      });
    },
    [enableRecentSearches, recentSearchesLimit]
  );

  function clearRecentSearches() {
    setRecent([]);
    try {
      window.localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch {
      // ignore
    }
  }

  function startVoiceSearch() {
    const SpeechRecognitionCtor = getSpeechRecognitionCtor();
    if (!SpeechRecognitionCtor) return;

    try {
      const recognition = new SpeechRecognitionCtor();
      recognition.lang = 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.onstart = () => setListening(true);
      recognition.onerror = () => setListening(false);
      recognition.onend = () => setListening(false);
      recognition.onresult = (event: SpeechRecognitionEventLike) => {
        const transcript = event.results?.[0]?.[0]?.transcript;
        if (transcript) {
          setQ(transcript);
          if (showSuggestions) setSuggestionsOpen(true);
        }
      };
      recognitionRef.current = recognition;
      recognition.start();
    } catch {
      setListening(false);
    }
  }

  const height = size === 'lg' ? 'h-14' : size === 'sm' ? 'h-10' : 'h-12';
  const containerRadius = variant === 'panel' ? 'rounded-2xl' : 'rounded-full';
  const buttonRadius = variant === 'panel' ? 'rounded-xl' : 'rounded-full';
  const idleLabel = submitLabel ?? (variant === 'panel' ? 'Search' : 'Go');

  const runSearch = React.useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) {
        setSuggestions([]);
        return;
      }

      try {
        abortRef.current?.abort();
      } catch {
        // ignore
      }
      const controller = new AbortController();
      abortRef.current = controller;

      setSuggestionsLoading(true);
      try {
        const params = new URLSearchParams({ q: trimmed });
        const res = await fetch(`/api/search?${params.toString()}`, { signal: controller.signal });
        if (!res.ok) {
          setSuggestions([]);
          return;
        }
        const js = await res.json().catch(() => ({} as { results?: unknown }));
        const list = Array.isArray(js?.results) ? (js.results as SearchResult[]) : [];
        setSuggestions(list.slice(0, maxSuggestions));
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        setSuggestions([]);
      } finally {
        if (!controller.signal.aborted) setSuggestionsLoading(false);
      }
    },
    [maxSuggestions]
  );

  React.useEffect(() => {
    if (!showSuggestions) return;
    if (!q.trim()) {
      setSuggestions([]);
      return;
    }
    const t = window.setTimeout(() => {
      void runSearch(q);
    }, 350);
    return () => window.clearTimeout(t);
  }, [q, runSearch, showSuggestions]);

  React.useEffect(() => {
    return () => {
      try {
        abortRef.current?.abort();
      } catch {
        // ignore
      }
      try {
        recognitionRef.current?.stop?.();
      } catch {
        // ignore
      }
    };
  }, []);

  function navigate(chain: string, address: string) {
    router.push(toWalletPath(chain, address));
  }

  function navigateTx(chain: string, hash: string) {
    router.push(toTxPath(chain, hash));
  }

  function navigateToResult(r: SearchResult) {
    pushRecentSearch(q);
    if (r?.kind === 'tx' && typeof r?.hash === 'string' && r.hash) {
      const chain = normalizeChain(r.chain || r.network) || defaultChain();
      navigateTx(chain, r.hash);
      return;
    }

    const address = typeof r?.address === 'string' ? r.address : undefined;
    if (!address) return;

    const chain = normalizeChain(r.chain || r.network) || defaultChain();
    navigate(chain, address);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next = q.trim();
    if (!next) return;

    pushRecentSearch(next);
    setLoading(true);
    setError(null);
    setSuggestionsOpen(false);

    try {
      // Try search first so we can pick the right chain when available.
      try {
        const params = new URLSearchParams({ q: next });
        const res = await fetch(`/api/search?${params.toString()}`);
        if (res.ok) {
          const js: SearchApiResponse = await res.json().catch(() => ({} as SearchApiResponse));

          if (js?.kind === 'tx' && js?.tx?.hash) {
            const chain = normalizeChain(js.tx.chain) || defaultChain();
            navigateTx(chain, js.tx.hash);
            return;
          }

          const list = Array.isArray(js?.results) ? (js.results as SearchResult[]) : [];
          const firstWallet = list.find((x) => typeof x?.address === 'string');
          if (firstWallet?.address) {
            navigateToResult(firstWallet);
            return;
          }
        }
      } catch {
        // ignore and fall back to resolve
      }

      // Fallback: resolve ENS/Base name/UD/address to an address (and chainHint).
      const r = await fetch(`/api/resolve-wallet?q=${encodeURIComponent(next)}`);
      if (!r.ok) {
        const js = await r.json().catch(() => ({} as { message?: string }));
        setError(String(js?.message || 'No results found.'));
        return;
      }
      const resolved = (await r.json().catch(() => null)) as ResolveWalletResponse | null;
      if (resolved && resolved.kind === 'lightning' && resolved.route) {
        router.push(String(resolved.route));
        return;
      }
      if (!resolved || !('address' in resolved) || !resolved.address) {
        setError('No results found.');
        return;
      }
      const chain = normalizeChain(resolved.chainHint) || defaultChain();
      navigate(chain, resolved.address);
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className={className}>
      <div className="relative">
        <div
          className={`flex items-center gap-2 ${containerRadius} border border-white/[0.10] bg-[#12151c]/70 px-3 ${height} shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_16px_36px_-20px_rgba(0,0,0,0.7)] focus-within:ring-2 focus-within:ring-[#00D4FF]/35`}
        >
          <span aria-hidden="true" className="inline-flex items-center justify-center text-[#9aa1ac]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              if (showSuggestions) setSuggestionsOpen(true);
            }}
            autoFocus={autoFocus}
            onFocus={() => {
              if (showSuggestions) setSuggestionsOpen(true);
            }}
            onBlur={() => {
              // Delay closing so click events on suggestions can fire
              window.setTimeout(() => setSuggestionsOpen(false), 120);
            }}
            // Some global/UA styles (especially WebKit autofill/text-fill) can make typed text effectively invisible.
            // Force the actual rendered text color + caret color here to keep it readable.
            style={{
              color: 'rgb(248 250 252)',
              WebkitTextFillColor: 'rgb(248 250 252)',
              caretColor: 'rgb(248 250 252)',
            } as React.CSSProperties}
            // FIX: long placeholder text ("Search wallet or tx hash (address,
            // ENS, UD, 0x… tx)") was hard-clipped mid-word in the compact
            // header search box with no ellipsis. `truncate` (overflow +
            // text-overflow: ellipsis) applies to placeholder text too.
            className={`min-w-0 flex-1 truncate bg-transparent outline-none placeholder:text-slate-300/70 !text-slate-50 caret-slate-50 ${
              inputClassName || ''
            }`}
            placeholder={placeholder}
            inputMode="text"
            autoComplete="off"
          />
          {showVoiceButton && voiceSupported ? (
            <button
              type="button"
              onClick={startVoiceSearch}
              aria-label={listening ? 'Listening…' : 'Search by voice'}
              title={listening ? 'Listening…' : 'Search by voice'}
              className={`inline-flex shrink-0 items-center justify-center rounded-full p-2 transition ${
                listening
                  ? 'bg-[#00D4FF]/20 text-[#00D4FF] ring-2 ring-[#00D4FF]/40'
                  : 'text-[#9aa1ac] hover:bg-white/[0.06] hover:text-[#00D4FF]'
              }`}
            >
              <MicIcon size={16} />
            </button>
          ) : null}
          <button
            type="submit"
            disabled={loading || !q.trim()}
            className={`${buttonRadius} bg-gradient-to-r from-[#00D4FF] via-[#5B7FFF] to-[#7C3AED] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-[1.06] transition`}
          >
            {loading ? 'Searching…' : idleLabel}
          </button>
        </div>

        {enableRecentSearches && !q.trim() && recent.length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: 'var(--muted-text, #94a3b8)' }}>
              <ClockIcon size={13} />
              Recent:
            </span>
            {recent.map((term) => (
              <button
                key={term}
                type="button"
                onClick={() => {
                  setQ(term);
                  if (showSuggestions) setSuggestionsOpen(true);
                }}
                className="rounded-full border px-3 py-1 text-xs font-medium transition hover:border-[#00D4FF]/40 hover:text-[#00D4FF]"
                style={{
                  borderColor: 'var(--color-card-border, rgba(91,127,255,0.18))',
                  color: '#cbd5e1',
                  backgroundColor: 'rgba(91,127,255,0.06)',
                }}
              >
                {term}
              </button>
            ))}
            <button
              type="button"
              onClick={clearRecentSearches}
              aria-label="Clear recent searches"
              title="Clear recent searches"
              className="inline-flex items-center justify-center rounded-full p-1 text-[#9aa1ac] transition hover:text-[#ef4444]"
            >
              <CloseIcon size={13} />
            </button>
          </div>
        ) : null}

        {showSuggestions && suggestionsOpen && q.trim() && suggestionsLoading && suggestions.length === 0 ? (
          <div className="absolute left-0 right-0 mt-2 z-[200] overflow-hidden rounded-2xl border border-white/[0.10] bg-[#12151c]/[0.98] shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_20px_48px_-16px_rgba(0,0,0,0.75)]">
            <ul className="py-2" aria-label="Loading search results">
              {Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="px-3 py-2">
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-8 w-8 rounded-full bg-white/10" />
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <Skeleton className="h-3 w-1/2 bg-white/10 rounded" />
                      <Skeleton className="h-2.5 w-3/4 bg-white/10 rounded" />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {showSuggestions && suggestionsOpen && q.trim() && suggestions.length > 0 ? (
          <div className="absolute left-0 right-0 mt-2 z-[200] overflow-hidden rounded-2xl border border-white/[0.10] bg-[#12151c]/[0.98] shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_20px_48px_-16px_rgba(0,0,0,0.75)]">
            <ul className="max-h-[320px] overflow-auto py-2">
              {suggestions.map((r, idx) => {
                const title = r.socials?.displayName || r.name || r.ens || r.id || r.address || 'Wallet';
                const subtitle = r.summary || r.description || (r.address ? String(r.address) : '');
                const trustScore = typeof r.trustScore === 'number' ? r.trustScore : undefined;
                const riskScore = typeof r.riskScore === 'number' ? r.riskScore : undefined;
                const flagsCount = typeof r.flagsCount === 'number' ? r.flagsCount : 0;
                const avatar = r.socials?.avatarUrl;
                const chain = normalizeChain(r.chain || r.network);

                return (
                  <li key={idx}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => navigateToResult(r)}
                      className="w-full px-3 py-2 text-left hover:bg-white/10 focus:bg-white/10 focus:outline-none"
                    >
                      <div className="flex items-start gap-3">
                        {/* FIX-49: real avatar when set (and on load failure
                            falls back automatically), otherwise a
                            WalletIdenticon keyed on this result's address -
                            replaces the old generic "W" placeholder. */}
                        <WalletAvatar
                          address={r.address}
                          avatarUrl={avatar}
                          size={32}
                          className="h-8 w-8 rounded-full object-cover border border-white/10"
                          alt=""
                        />

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-xs sm:text-sm font-semibold text-slate-50 truncate">{title}</div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {chain ? (
                                <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold text-slate-100">
                                  {chain}
                                </span>
                              ) : null}
                              {r.blacklisted ? (
                                <span className="rounded-full bg-red-600/20 border border-red-400/30 px-2 py-0.5 text-[10px] font-semibold text-red-200">
                                  Blacklisted
                                </span>
                              ) : null}
                              {r.kycStatus === 'verified' ? (
                                <span className="rounded-full bg-emerald-500/15 border border-emerald-400/30 px-2 py-0.5 text-[10px] font-semibold text-emerald-200">
                                  Verified
                                </span>
                              ) : null}
                            </div>
                          </div>

                          {subtitle ? (
                            <div className="mt-0.5 text-[11px] text-slate-200/85 line-clamp-2">{subtitle}</div>
                          ) : null}

                          <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-200/90">
                            {trustScore !== undefined ? (
                              <span className="rounded-full bg-white/5 border border-white/10 px-2 py-0.5">
                                Trust {Math.round(trustScore)}
                              </span>
                            ) : null}
                            {riskScore !== undefined ? (
                              <span className="rounded-full bg-amber-500/10 border border-amber-400/20 px-2 py-0.5 text-amber-100">
                                Risk {Math.round(riskScore)}
                              </span>
                            ) : null}
                            {flagsCount > 0 ? (
                              <span className="rounded-full bg-white/5 border border-white/10 px-2 py-0.5">
                                {flagsCount} flags
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      {error ? <div className="mt-2 text-xs text-rose-300">{error}</div> : null}
    </form>
  );
}
